import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Plus, MessageSquare, CheckCircle2, Clock } from "lucide-react";
import { toast } from "sonner";

const STATUS_LABEL = { open: "Aberto", in_progress: "Em atendimento", resolved: "Resolvido" };
const STATUS_COLOR = {
  open: "bg-amber-100 text-amber-800",
  in_progress: "bg-blue-100 text-blue-800",
  resolved: "bg-emerald-100 text-emerald-800",
};

const FAQ = [
  { q: "Como funciona a Aula Completa?", a: "Além do plano, geramos todo o conteúdo que você pode usar em sala: explicação passo a passo, exemplos resolvidos, perguntas, atividades com gabarito e tarefa de casa." },
  { q: "Como funciona o plano Premium?", a: "R$ 19,90 por mês, cobrança recorrente processada pelo Asaas. Você pode cancelar quando quiser diretamente pelo painel do Asaas enviado em cada cobrança por e-mail." },
  { q: "O que acontece após as 5 gerações grátis?", a: "A conta continua, mas novas gerações ficam bloqueadas até você assinar o Premium." },
  { q: "O PDF tem marca d'água?", a: "Não. O PDF é limpo, pronto para impressão e aplicação em sala." },
  { q: "Como falar com o suporte?", a: "Abra um chamado aqui ou escreva para aulapronta01@gmail.com." },
];

export default function SupportPage() {
  const [tickets, setTickets] = useState([]);
  const [categories, setCategories] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [open, setOpen] = useState(null);
  const [form, setForm] = useState({ category: "", subject: "", message: "" });
  const [reply, setReply] = useState("");
  const [loading, setLoading] = useState(false);

  const load = () => api.get("/support/tickets").then(r => setTickets(r.data)).catch(() => {});

  useEffect(() => {
    api.get("/support/categories").then(r => setCategories(r.data.categories));
    load();
  }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!form.category) return toast.error("Escolha uma categoria.");
    setLoading(true);
    try {
      const { data } = await api.post("/support/tickets", form);
      toast.success(`Chamado #${data.number} aberto.`);
      setForm({ category: "", subject: "", message: "" });
      setShowForm(false);
      await load();
      setOpen(data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };

  const sendReply = async () => {
    if (!reply.trim() || !open) return;
    setLoading(true);
    try {
      const { data } = await api.post(`/support/tickets/${open.id}/reply`, { message: reply });
      setReply("");
      setOpen(data);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };

  return (
    <div className="p-6 lg:p-10 max-w-5xl mx-auto">
      <h1 className="font-heading text-3xl sm:text-4xl font-extrabold text-slate-900">Ajuda e Suporte</h1>
      <p className="mt-2 text-slate-600">Precisa de ajuda? Abra um chamado ou consulte as perguntas frequentes.</p>

      <section className="mt-8">
        <h2 className="font-heading text-xl font-bold text-slate-900 mb-3">Perguntas frequentes</h2>
        <div className="space-y-2">
          {FAQ.map(f => (
            <details key={f.q} className="group bg-white rounded-xl border border-slate-200 p-4" data-testid="faq-item">
              <summary className="cursor-pointer font-semibold text-slate-900 list-none flex items-center justify-between">
                {f.q} <span className="text-[#8B5CF6] group-open:rotate-45 transition-transform">+</span>
              </summary>
              <p className="mt-2 text-sm text-slate-600">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      <section className="mt-10">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-heading text-xl font-bold text-slate-900">Meus chamados</h2>
          <Button onClick={() => setShowForm(v => !v)} className="rounded-full bg-[#1F3A8A] hover:bg-[#15265b]" data-testid="btn-new-ticket">
            <Plus className="mr-2" size={16}/> Novo chamado
          </Button>
        </div>

        {showForm && (
          <form onSubmit={create} className="bg-white rounded-2xl border border-slate-200 p-5 space-y-3 mb-5">
            <div>
              <Label className="text-sm font-semibold text-slate-700">Categoria</Label>
              <Select value={form.category} onValueChange={v => setForm({...form, category: v})}>
                <SelectTrigger className="rounded-xl h-11 mt-1" data-testid="ticket-category"><SelectValue placeholder="Escolha..."/></SelectTrigger>
                <SelectContent>{categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm font-semibold text-slate-700">Assunto</Label>
              <Input required value={form.subject} onChange={e => setForm({...form, subject: e.target.value})}
                className="rounded-xl h-11 mt-1" placeholder="Resumo do problema" data-testid="ticket-subject"/>
            </div>
            <div>
              <Label className="text-sm font-semibold text-slate-700">Mensagem</Label>
              <Textarea required value={form.message} onChange={e => setForm({...form, message: e.target.value})}
                className="rounded-xl mt-1 min-h-[120px]" placeholder="Descreva o que aconteceu..." data-testid="ticket-message"/>
            </div>
            <Button disabled={loading} className="rounded-full bg-[#8B5CF6] hover:bg-[#7c4ef4]" data-testid="btn-send-ticket">
              {loading && <Loader2 className="animate-spin mr-2" size={14}/>} Abrir chamado
            </Button>
          </form>
        )}

        {open ? (
          <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid="ticket-detail">
            <button onClick={() => setOpen(null)} className="text-sm text-slate-500 hover:text-slate-900 mb-3">← Voltar</button>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <div className="text-xs uppercase tracking-widest text-[#8B5CF6] font-bold">#{open.number} · {open.category}</div>
                <h3 className="font-heading text-lg font-bold text-slate-900">{open.subject}</h3>
              </div>
              <span className={`px-3 py-1 rounded-full text-xs font-bold ${STATUS_COLOR[open.status]}`}>{STATUS_LABEL[open.status]}</span>
            </div>
            <div className="mt-4 space-y-3 max-h-96 overflow-y-auto">
              {(open.messages || []).map(m => (
                <div key={m.id} className={`p-3 rounded-xl ${m.author === "admin" ? "bg-[#1F3A8A]/5 border-l-4 border-[#1F3A8A]" : "bg-slate-50"}`}>
                  <div className="text-xs text-slate-500 font-semibold mb-1">{m.author_name} · {new Date(m.created_at).toLocaleString("pt-BR")}</div>
                  <div className="text-sm text-slate-800 whitespace-pre-wrap">{m.message}</div>
                </div>
              ))}
            </div>
            {open.status !== "resolved" && (
              <div className="mt-4 flex gap-2">
                <Input value={reply} onChange={e => setReply(e.target.value)} placeholder="Responder..." className="rounded-xl" data-testid="ticket-reply-input"/>
                <Button onClick={sendReply} disabled={loading || !reply.trim()} className="rounded-full bg-[#1F3A8A]" data-testid="btn-send-reply">Enviar</Button>
              </div>
            )}
          </div>
        ) : tickets.length === 0 ? (
          <div className="bg-white rounded-2xl border border-dashed border-slate-300 p-8 text-center">
            <MessageSquare className="mx-auto text-slate-400" size={32}/>
            <p className="mt-2 text-slate-600">Você ainda não abriu nenhum chamado.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {tickets.map(t => (
              <button key={t.id} onClick={() => setOpen(t)}
                className="w-full text-left bg-white border border-slate-200 rounded-xl p-4 hover:border-[#8B5CF6]/50"
                data-testid={`ticket-row-${t.id}`}>
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div>
                    <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">#{t.number} · {t.category}</div>
                    <div className="font-semibold text-slate-900">{t.subject}</div>
                    <div className="text-xs text-slate-500 mt-1">{new Date(t.updated_at).toLocaleString("pt-BR")}</div>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-bold ${STATUS_COLOR[t.status]}`}>
                    {t.status === "resolved" ? <CheckCircle2 className="inline mr-1" size={12}/> : <Clock className="inline mr-1" size={12}/>}
                    {STATUS_LABEL[t.status]}
                  </span>
                </div>
              </button>
            ))}
          </div>
        )}
      </section>

      <p className="mt-10 text-center text-sm text-slate-500">
        Também pode escrever para <a href="mailto:aulapronta01@gmail.com" className="font-semibold text-[#1F3A8A]">aulapronta01@gmail.com</a>
      </p>
    </div>
  );
}
